import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { User, Loader2, CheckCircle, FileCheck, ArrowLeft, Users, Shield } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const DECLARATIONS_CONTRAT = [
  "Pieces d'identite obligatoire deposee avant la location.",
  "Je reconnais avoir recu le materiel en parfait etat de fonctionnement.",
  "Je reconnais avoir ete informe de la zone de navigation autorisee et je m'engage a la respecter.",
  "Je declare etre en mesure d'effectuer une navigation en autonomie.",
  "Je m'engage a appliquer les regles de navigation usuelles en France.",
  "Je reconnais ma responsabilite en cas de dommage a un tiers ou a l'equipement loue.",
  "J'autorise le responsable technique qualifie a prendre toutes les mesures d'urgence en cas d'accident.",
];

const DECLARATIONS_MAJEUR = [
  "J'atteste etre capable de plonger avec immersion puis de nager au moins 25m sans flottabilite.",
  "Je declare avoir pris connaissance et accepter sans reserve le reglement interieur de la base nautique.",
  "J'atteste avoir recu une information precisant le montant des garanties d'assurance associees aux activites nautiques de la base.",
];

const GABARIT_OPTIONS = [
  { value: 'Petit', label: 'Petit (< 60kg)' },
  { value: 'Moyen', label: 'Moyen (60-80kg)' },
  { value: 'Grand', label: 'Grand (80-100kg)' },
  { value: 'Tres Grand', label: 'Tres Grand (> 100kg)' },
];

const LEVEL_OPTIONS = [
  { value: 'Debutant', label: 'Debutant' },
  { value: 'Intermediaire', label: 'Intermediaire' },
  { value: 'Confirme', label: 'Confirme' },
  { value: 'Expert', label: 'Expert' },
];

const emptyParticipant = () => ({ first_name: '', last_name: '', age: '', gabarit: '', level: '' });

export default function ParticipantsPage() {
  const { bookingId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const authToken = localStorage.getItem('rw_token');
  const { user } = useAuth();
  const navigate = useNavigate();

  const [booking, setBooking] = useState(null);
  const [requiredCount, setRequiredCount] = useState(1);
  const [participants, setParticipants] = useState([]);
  const [existing, setExisting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);
  const [acceptedDeclarations, setAcceptedDeclarations] = useState({});

  const allDeclarationsCount = DECLARATIONS_CONTRAT.length + DECLARATIONS_MAJEUR.length;
  const acceptedCount = Object.values(acceptedDeclarations).filter(Boolean).length;
  const allDeclarationsAccepted = acceptedCount === allDeclarationsCount;

  useEffect(() => {
    if (token) {
      axios.get(`${API}/api/bookings/${bookingId}/info?token=${token}`)
        .then(r => {
          const data = r.data;
          setBooking(data.booking);
          setRequiredCount(data.required_count);
          if (data.existing_participants && data.existing_participants.length > 0) {
            setExisting({ participants: data.existing_participants });
            setDone(true);
          }
        })
        .catch(err => {
          setError(err.response?.data?.detail || 'Lien invalide ou expire');
        })
        .finally(() => setLoading(false));
      return;
    }

    if (!authToken) { navigate('/auth'); return; }
    const headers = { Authorization: `Bearer ${authToken}` };
    Promise.all([
      axios.get(`${API}/api/bookings/my`, { headers }).then(r => {
        const allBookings = r.data;
        const match = allBookings.find(bk => bk.id === bookingId || bk.group_id === bookingId);
        if (!match) return null;
        const groupId = match.group_id || match.id;
        const groupBookings = allBookings.filter(bk => bk.group_id === groupId || bk.id === groupId);
        const totalQty = groupBookings.reduce((sum, bk) => sum + (bk.quantity || 1), 0);
        setBooking(match);
        setRequiredCount(totalQty);
        return { booking: match, totalQty };
      }),
      axios.get(`${API}/api/bookings/${bookingId}/participants`, { headers }).then(r => {
        if (r.data.participants && r.data.participants.length > 0) {
          setExisting(r.data);
          setDone(true);
        }
        return r.data;
      }).catch(() => null),
    ]).catch(() => {
      toast.error('Impossible de charger la reservation');
    }).finally(() => setLoading(false));
  }, [bookingId, token, authToken, navigate]);

  // Initialize participant forms
  useEffect(() => {
    if (done || requiredCount < 1) return;
    const forms = Array.from({ length: requiredCount }, () => emptyParticipant());
    if (token && booking) {
      const names = (booking.client_name || '').split(' ');
      forms[0] = { ...forms[0], first_name: names[0] || '', last_name: names.slice(1).join(' ') || '' };
    } else if (user && forms.length > 0) {
      const names = (user.name || '').split(' ');
      forms[0] = {
        first_name: names[0] || '',
        last_name: names.slice(1).join(' ') || '',
        age: user.age || '',
        gabarit: user.gabarit || user.size || '',
        level: user.niveau_pratique || user.practice_level || '',
      };
    }
    setParticipants(forms);
  }, [requiredCount, user, done, token, booking]);

  const updateParticipant = (idx, field, value) => {
    setParticipants(prev => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));
  };

  const handleSubmit = async () => {
    const valid = participants.every(p => p.first_name.trim() && p.last_name.trim());
    if (!valid) { toast.error('Nom et prenom sont requis pour chaque participant'); return; }
    if (!allDeclarationsAccepted) { toast.error('Veuillez accepter toutes les declarations du contrat de location'); return; }

    setSubmitting(true);
    try {
      const payload = {
        participants: participants.map(p => ({
          first_name: p.first_name.trim(),
          last_name: p.last_name.trim(),
          age: p.age ? parseInt(p.age) : null,
          gabarit: p.gabarit || null,
          level: p.level || null,
        })),
      };

      if (token) {
        await axios.post(`${API}/api/bookings/${bookingId}/participants/register?token=${token}`, payload);
      } else {
        await axios.post(`${API}/api/bookings/${bookingId}/participants`, payload, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
      }
      toast.success('Participants enregistres ! Vous recevrez le contrat PDF par email.');
      setDone(true);
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
      <Loader2 className="w-8 h-8 animate-spin text-[#00FF00]" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#0A0A0A] text-white p-4">
      <p className="text-red-400 mb-4">{error}</p>
      <p className="text-slate-500 text-sm">Si le probleme persiste, contactez Riding Watt.</p>
    </div>
  );

  if (!booking) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#0A0A0A] text-white p-4">
      <p className="text-slate-400 mb-4">Reservation introuvable ou vous n'avez pas acces.</p>
      {!token && (
        <Button onClick={() => navigate('/dashboard')} variant="outline" className="border-[#2A2A2A] text-white">
          <ArrowLeft className="w-4 h-4 mr-2" /> Retour
        </Button>
      )}
    </div>
  );

  // ===== DONE STATE =====
  if (done) return (
    <div className="min-h-screen bg-[#0A0A0A] text-white p-4 sm:p-8">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <CheckCircle className="w-16 h-16 text-[#00FF00] mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Participants enregistres !</h1>
          <p className="text-slate-400">Vous recevrez un email avec le contrat en PDF.</p>
        </div>
        {existing && (
          <div className="bg-[#1A1A1A] rounded-2xl border border-[#2A2A2A] p-6 space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <FileCheck className="w-5 h-5 text-[#00FF00]" />
              <h2 className="font-bold text-lg">{booking.activity_name}</h2>
              <Badge className="bg-[#00FF00]/15 text-[#00FF00] border-0 ml-auto">{booking.date} - {booking.time_slot}</Badge>
            </div>
            {existing.participants.map((p, i) => (
              <div key={i} className="bg-[#0A0A0A] rounded-xl p-4 border border-[#2A2A2A] flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-[#00FF00]/10 flex items-center justify-center">
                  <User className="w-5 h-5 text-[#00FF00]" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-white">{p.first_name} {p.last_name}</p>
                  <div className="flex gap-3 text-xs text-slate-400 mt-1">
                    {p.age && <span>{p.age} ans</span>}
                    {p.gabarit && <span>{p.gabarit}</span>}
                    {p.level && <span>{p.level}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {!token && (
          <div className="text-center mt-6">
            <Button onClick={() => navigate('/dashboard')} className="bg-[#00FF00] text-black hover:bg-[#00CC00] font-bold">
              Retour au tableau de bord
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  // ===== PARTICIPANT FORM =====
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white p-4 sm:p-8" data-testid="participants-page">
      <div className="max-w-2xl mx-auto">
        {!token && (
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-400 hover:text-white mb-6 text-sm transition-colors">
            <ArrowLeft className="w-4 h-4" /> Retour
          </button>
        )}

        <div className="text-center mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">Enregistrement des participants</h1>
          <p className="text-slate-400 text-sm">Renseignez les informations de chaque participant</p>
        </div>

        {/* Booking Summary */}
        <div className="bg-[#1A1A1A] rounded-2xl border border-[#2A2A2A] p-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-bold text-white">{booking.activity_name}</p>
              <p className="text-xs text-slate-400">{booking.date} - {booking.time_slot} - {booking.duration_minutes} min</p>
            </div>
            <Badge className="bg-[#00FF00]/15 text-[#00FF00] border-0 font-bold">{booking.price}EUR</Badge>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-4">
          <Users className="w-4 h-4 text-[#FF6600]" />
          <span className="text-sm font-medium text-[#FF6600]" data-testid="required-participants-count">
            {requiredCount} participant{requiredCount > 1 ? 's' : ''} a enregistrer
          </span>
        </div>

        <div className="space-y-4" data-testid="participants-list">
          {participants.map((p, idx) => (
            <div key={idx} className="bg-[#1A1A1A] rounded-2xl border border-[#2A2A2A] p-5" data-testid={`participant-${idx}`}>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-full bg-[#00FF00]/10 flex items-center justify-center">
                  <User className="w-4 h-4 text-[#00FF00]" />
                </div>
                <span className="font-semibold text-sm">Participant {idx + 1}/{requiredCount}</span>
                {idx === 0 && <Badge className="bg-blue-500/15 text-blue-400 border-0 text-[10px]">Titulaire</Badge>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-400 mb-1 block">Prenom *</Label>
                  <Input value={p.first_name} onChange={e => updateParticipant(idx, 'first_name', e.target.value)} placeholder="Prenom" className="bg-[#0A0A0A] border-[#2A2A2A] text-white text-sm" data-testid={`participant-${idx}-first-name`} />
                </div>
                <div>
                  <Label className="text-xs text-slate-400 mb-1 block">Nom *</Label>
                  <Input value={p.last_name} onChange={e => updateParticipant(idx, 'last_name', e.target.value)} placeholder="Nom" className="bg-[#0A0A0A] border-[#2A2A2A] text-white text-sm" data-testid={`participant-${idx}-last-name`} />
                </div>
                <div>
                  <Label className="text-xs text-slate-400 mb-1 block">Age</Label>
                  <Input type="number" value={p.age} onChange={e => updateParticipant(idx, 'age', e.target.value)} placeholder="Age" min={1} max={99} className="bg-[#0A0A0A] border-[#2A2A2A] text-white text-sm" data-testid={`participant-${idx}-age`} />
                </div>
                <div>
                  <Label className="text-xs text-slate-400 mb-1 block">Gabarit</Label>
                  <select value={p.gabarit} onChange={e => updateParticipant(idx, 'gabarit', e.target.value)} className="w-full h-9 px-3 rounded-md bg-[#0A0A0A] border border-[#2A2A2A] text-white text-sm" data-testid={`participant-${idx}-gabarit`}>
                    <option value="">Choisir...</option>
                    {GABARIT_OPTIONS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs text-slate-400 mb-1 block">Niveau de pratique</Label>
                  <select value={p.level} onChange={e => updateParticipant(idx, 'level', e.target.value)} className="w-full h-9 px-3 rounded-md bg-[#0A0A0A] border border-[#2A2A2A] text-white text-sm" data-testid={`participant-${idx}-level`}>
                    <option value="">Choisir...</option>
                    {LEVEL_OPTIONS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Contract Declarations Section */}
        <div className="mt-6 bg-[#1A1A1A] rounded-2xl border border-[#2A2A2A] p-5" data-testid="contract-declarations-section">
          <div className="flex items-center gap-2 mb-4">
            <Shield className={`w-5 h-5 ${allDeclarationsAccepted ? 'text-[#00FF00]' : 'text-[#FF6600]'}`} />
            <span className="font-semibold text-sm text-white">Contrat de location</span>
            <Badge className={`ml-auto border-0 text-[10px] ${allDeclarationsAccepted ? 'bg-[#00FF00]/15 text-[#00FF00]' : 'bg-[#FF6600]/15 text-[#FF6600]'}`}>
              {acceptedCount}/{allDeclarationsCount}
            </Badge>
          </div>

          <div className="mb-4">
            <p className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-3">Conditions de location</p>
            <div className="space-y-2.5">
              {DECLARATIONS_CONTRAT.map((decl, i) => (
                <label key={`cond-${i}`} className="flex items-start gap-2.5 cursor-pointer group" data-testid={`condition-${i}`}>
                  <input
                    type="checkbox"
                    checked={!!acceptedDeclarations[`cond-${i}`]}
                    onChange={e => setAcceptedDeclarations(prev => ({ ...prev, [`cond-${i}`]: e.target.checked }))}
                    className="mt-0.5 accent-[#00FF00] w-3.5 h-3.5 flex-shrink-0"
                    data-testid={`condition-checkbox-${i}`}
                  />
                  <span className={`text-xs leading-relaxed transition-colors ${acceptedDeclarations[`cond-${i}`] ? 'text-white' : 'text-slate-500 group-hover:text-slate-300'}`}>
                    {decl}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="border-t border-[#2A2A2A] pt-4">
            <p className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-3">Declarations du participant (+18 ans)</p>
            <div className="space-y-2.5">
              {DECLARATIONS_MAJEUR.map((decl, i) => (
                <label key={`decl-${i}`} className="flex items-start gap-2.5 cursor-pointer group" data-testid={`declaration-${i}`}>
                  <input
                    type="checkbox"
                    checked={!!acceptedDeclarations[`decl-${i}`]}
                    onChange={e => setAcceptedDeclarations(prev => ({ ...prev, [`decl-${i}`]: e.target.checked }))}
                    className="mt-0.5 accent-[#00FF00] w-3.5 h-3.5 flex-shrink-0"
                    data-testid={`declaration-checkbox-${i}`}
                  />
                  <span className={`text-xs leading-relaxed transition-colors ${acceptedDeclarations[`decl-${i}`] ? 'text-white' : 'text-slate-500 group-hover:text-slate-300'}`}>
                    {decl}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {!allDeclarationsAccepted && (
            <button
              onClick={() => {
                const all = {};
                DECLARATIONS_CONTRAT.forEach((_, i) => all[`cond-${i}`] = true);
                DECLARATIONS_MAJEUR.forEach((_, i) => all[`decl-${i}`] = true);
                setAcceptedDeclarations(all);
              }}
              className="w-full text-center text-xs text-[#00FF00] hover:text-[#00CC00] underline underline-offset-2 transition-colors py-2 mt-3"
              data-testid="accept-all-declarations-btn"
            >
              J'accepte toutes les conditions et declarations
            </button>
          )}
        </div>

        <Button
          onClick={handleSubmit}
          disabled={submitting || !participants.every(p => p.first_name.trim() && p.last_name.trim()) || !allDeclarationsAccepted}
          className="w-full mt-6 bg-[#00FF00] text-black hover:bg-[#00CC00] font-bold py-6 text-base disabled:opacity-50"
          data-testid="submit-participants-btn"
        >
          {submitting ? (
            <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Enregistrement...</>
          ) : (
            <><CheckCircle className="w-4 h-4 mr-2" /> Enregistrer et signer le contrat</>
          )}
        </Button>

        <p className="text-xs text-slate-600 text-center mt-3">
          En cliquant, vous signez electroniquement le contrat de location. Un PDF vous sera envoye par email.
        </p>
      </div>
    </div>
  );
}
